import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../landing/Navbar";
import Footer from "../landing/Footer";
import { ArrowUp } from "lucide-react";

/**
 * Shared layout for legal pages (Privacy, Cookies, Acceptable Use).
 * Mirrors the TermsPage visual style: hero strip + sticky TOC + sections.
 */
export default function LegalLayout({
  eyebrow,
  title,
  intro,
  effective,
  lastUpdated,
  sections,
  testidPrefix,
}) {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          100,
        );
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <div
      className="min-h-screen bg-cream-50 text-moss-900"
      data-testid={`${testidPrefix}-page`}
      id="top"
    >
      <Navbar />

      <section className="pt-32 md:pt-40 pb-12 md:pb-16 paper-texture relative overflow-hidden">
        <div className="absolute -top-24 right-0 w-[420px] h-[420px] bg-terracotta-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] bg-moss-200/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-terracotta-100 text-terracotta-800 rounded-full text-xs font-medium border border-terracotta-200/60">
            {eyebrow}
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-moss-900 text-balance">
            {title}
          </h1>
          {intro && (
            <p className="mt-5 text-base md:text-lg text-moss-700 max-w-3xl leading-relaxed">
              {intro}
            </p>
          )}
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-moss-600">
            {effective && (
              <span>
                <span className="font-semibold text-moss-900">Effective:</span>{" "}
                {effective}
              </span>
            )}
            {lastUpdated && (
              <span>
                <span className="font-semibold text-moss-900">
                  Last updated:
                </span>{" "}
                {lastUpdated}
              </span>
            )}
            <span>
              <span className="font-semibold text-moss-900">
                Governing law:
              </span>{" "}
              England &amp; Wales
            </span>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <aside
              className="lg:col-span-3"
              data-testid={`${testidPrefix}-toc`}
            >
              <nav className="lg:sticky lg:top-28 rounded-2xl border border-moss-200/40 bg-cream-100/60 p-5">
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-moss-700 mb-3">
                  Contents
                </p>
                <ol className="space-y-1.5">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="block text-sm text-moss-700 hover:text-terracotta-700 hover:bg-cream-50 rounded-md px-2 py-1 transition-colors"
                        data-testid={`${testidPrefix}-toc-${s.id}`}
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>

            <article
              className="lg:col-span-9 max-w-3xl text-moss-800"
              data-testid={`${testidPrefix}-content`}
            >
              {sections.map((s, i) => (
                <section
                  key={s.id}
                  id={s.id}
                  className={`scroll-mt-28 ${i === 0 ? "" : "mt-14"}`}
                  data-testid={`${testidPrefix}-section-${s.id}`}
                >
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-moss-900 leading-tight">
                    {s.label}
                  </h2>
                  <div className="mt-4 space-y-4 text-[15px] sm:text-base leading-relaxed text-moss-800">
                    {s.content}
                  </div>
                </section>
              ))}

              <div className="mt-12 pt-8 border-t border-moss-200/50 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
                <p className="text-sm text-moss-600">
                  Questions? Email{" "}
                  <a
                    href="mailto:support@binshare.co.uk"
                    className="text-terracotta-700 underline underline-offset-2"
                  >
                    support@binshare.co.uk
                  </a>
                  .
                </p>
                <a
                  href="#top"
                  className="inline-flex items-center gap-2 text-sm font-medium text-terracotta-700 hover:text-terracotta-800"
                  data-testid={`${testidPrefix}-back-to-top`}
                >
                  Back to top <ArrowUp className="w-4 h-4" />
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
